import React, { useState, useMemo } from 'react';
import { useError } from '../../../context/ErrorContext';
import Toggle from '../../../components/ui/Toggle';

interface PointsConfigSectionProps {
  pointsPerVisit: number;
  setPointsPerVisit: (points: number) => void;
  timeBetweenVisits: number;
  setTimeBetweenVisits: (minutes: number) => void;
  allowPointsAccumulation: boolean;
  setAllowPointsAccumulation: (allow: boolean) => void;
}

// Presets de puntos por visita
const POINTS_PRESETS = [
  { label: '1 punto', value: 1 },
  { label: '5 puntos', value: 5 },
  { label: '10 puntos', value: 10 },
  { label: '15 puntos', value: 15 },
  { label: '20 puntos', value: 20 },
  { label: '25 puntos', value: 25 },
  { label: '50 puntos', value: 50 },
  { label: '100 puntos', value: 100 },
];

// Presets de tiempo en minutos con etiquetas legibles
const TIME_PRESETS = [
  { label: '15 minutos', value: 15 },
  { label: '30 minutos', value: 30 },
  { label: '1 hora', value: 60 },
  { label: '2 horas', value: 120 },
  { label: '4 horas', value: 240 },
  { label: '8 horas', value: 480 },
  { label: '12 horas', value: 720 },
  { label: '1 día', value: 1440 },
  { label: '2 días', value: 2880 },
  { label: '7 días', value: 10080 },
];

const CUSTOM_VALUE = 'custom';

// Helper: convierte minutos a texto legible
const formatMinutesToLabel = (minutes: number): string => {
  if (minutes < 60) return `${minutes} min`;
  if (minutes < 1440) {
    const hours = Math.floor(minutes / 60);
    const remainingMin = minutes % 60;
    return remainingMin > 0 ? `${hours}h ${remainingMin}min` : `${hours}h`;
  }
  const days = Math.floor(minutes / 1440);
  const remainingHours = Math.floor((minutes % 1440) / 60);
  return remainingHours > 0 ? `${days}d ${remainingHours}h` : `${days}d`;
};

const PointsConfigSection: React.FC<PointsConfigSectionProps> = ({
  pointsPerVisit,
  setPointsPerVisit,
  timeBetweenVisits,
  setTimeBetweenVisits,
  allowPointsAccumulation,
  setAllowPointsAccumulation
}) => {
  const { addError, addValidationError } = useError();
  const [updating, setUpdating] = useState<boolean>(false);

  // Determinar si los valores actuales coinciden con algún preset
  const isPointsPreset = useMemo(
    () => POINTS_PRESETS.some(p => p.value === pointsPerVisit),
    [pointsPerVisit]
  );

  const isTimePreset = useMemo(
    () => TIME_PRESETS.some(p => p.value === timeBetweenVisits),
    [timeBetweenVisits]
  );

  // Estados para controlar si se muestra el input personalizado
  const [showCustomPoints, setShowCustomPoints] = useState<boolean>(!isPointsPreset);
  const [showCustomTime, setShowCustomTime] = useState<boolean>(!isTimePreset);

  // Valores actuales de los selects
  const pointsSelectValue = isPointsPreset && !showCustomPoints
    ? String(pointsPerVisit)
    : CUSTOM_VALUE;

  const timeSelectValue = isTimePreset && !showCustomTime
    ? String(timeBetweenVisits)
    : CUSTOM_VALUE;

  // Handler para el select de puntos
  const handlePointsSelectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selected = e.target.value;

    if (selected === CUSTOM_VALUE) {
      setShowCustomPoints(true);
      return;
    }

    setShowCustomPoints(false);
    const points = parseInt(selected);
    if (!isNaN(points)) {
      setPointsPerVisit(points);
    }
  };

  // Handler para el input personalizado de puntos
  const handleCustomPointsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    try {
      const valueAsString = e.target.value;
      const value = parseInt(valueAsString);

      if (isNaN(value)) {
        if (valueAsString !== '') {
          addValidationError('Los puntos por visita deben ser un número válido', 'points-config-validation');
        }
        return;
      }

      if (value < 1) {
        addValidationError('Los puntos por visita deben ser al menos 1', 'points-config-validation');
        return;
      }

      if (value > 1000) {
        addValidationError('Los puntos por visita no pueden ser mayores a 1000', 'points-config-validation');
        return;
      }

      setPointsPerVisit(value);
    } catch (error) {
      addError('Error al actualizar puntos por visita', 'validation', 'points-config-points-update');
      console.error('Error updating points per visit:', error);
    }
  };

  // Handler para el select de tiempo
  const handleTimeSelectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selected = e.target.value;

    if (selected === CUSTOM_VALUE) {
      setShowCustomTime(true);
      return;
    }

    setShowCustomTime(false);
    const minutes = parseInt(selected);
    if (!isNaN(minutes)) {
      setTimeBetweenVisits(minutes);
    }
  };

  // Handler para el input personalizado de minutos
  const handleCustomTimeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    try {
      const valueAsString = e.target.value;
      const value = parseInt(valueAsString);

      if (isNaN(value)) {
        if (valueAsString !== '') {
          addValidationError('El tiempo entre visitas debe ser un número válido', 'time-config-validation');
        }
        return;
      }

      if (value < 1) {
        addValidationError('El tiempo entre visitas debe ser al menos 1 minuto', 'time-config-validation');
        return;
      }

      if (value > 10080) {
        addValidationError('El tiempo entre visitas no puede ser mayor a 7 días (10080 minutos)', 'time-config-validation');
        return;
      }

      setTimeBetweenVisits(value);
    } catch (error) {
      addError('Error al actualizar tiempo entre visitas', 'validation', 'points-config-time-update');
      console.error('Error updating time between visits:', error);
    }
  };

  const handleToggleAccumulation = () => {
    try {
      setUpdating(true);
      setAllowPointsAccumulation(!allowPointsAccumulation);
    } catch (error) {
      addError('Error al cambiar la configuración de acumulación de puntos', 'validation', 'points-config-accumulation');
      console.error('Error toggling points accumulation:', error);
    } finally {
      setUpdating(false);
    }
  };

  return (
    <div className="bg-surface-2 p-4 rounded-lg">
      <h4 className="text-sm font-medium text-content-2 mb-1">Configuración de puntos y visitas</h4>
      <p className="text-xs text-content-3 mb-4">
        Configura cómo se otorgan y acumulan los puntos por visitas.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        {/* Puntos por visita */}
        <div>
          <label htmlFor="pointsPerVisit" className="block text-sm font-medium text-content-2 mb-2">
            Puntos por visita
          </label>
          <select
            id="pointsPerVisit"
            value={pointsSelectValue}
            onChange={handlePointsSelectChange}
            className="shadow-sm focus:ring-accent focus:border-blue-500 block w-full sm:text-sm border-line rounded-md"
          >
            {POINTS_PRESETS.map(preset => (
              <option key={preset.value} value={String(preset.value)}>
                {preset.label}
              </option>
            ))}
            <option value={CUSTOM_VALUE}>Personalizado</option>
          </select>

          {showCustomPoints && (
            <div className="mt-2">
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                value={pointsPerVisit}
                onChange={handleCustomPointsChange}
                placeholder="Ej: 35"
                className="shadow-sm focus:ring-accent focus:border-blue-500 block w-full sm:text-sm border-line rounded-md"
              />
            </div>
          )}

          <p className="mt-1 text-xs text-content-3">
            Número de puntos que se otorgan por cada visita.
          </p>
        </div>

        {/* Tiempo entre visitas */}
        <div>
          <label htmlFor="timeBetweenVisits" className="block text-sm font-medium text-content-2 mb-2">
            Tiempo entre visitas
          </label>
          <select
            id="timeBetweenVisits"
            value={timeSelectValue}
            onChange={handleTimeSelectChange}
            className="shadow-sm focus:ring-accent focus:border-blue-500 block w-full sm:text-sm border-line rounded-md"
          >
            {TIME_PRESETS.map(preset => (
              <option key={preset.value} value={String(preset.value)}>
                {preset.label}
              </option>
            ))}
            <option value={CUSTOM_VALUE}>Personalizado</option>
          </select>

          {showCustomTime && (
            <div className="mt-2">
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  value={timeBetweenVisits}
                  onChange={handleCustomTimeChange}
                  placeholder="Ej: 1200"
                  className="shadow-sm focus:ring-accent focus:border-blue-500 block w-full sm:text-sm border-line rounded-md"
                />
                <span className="text-xs text-content-3 whitespace-nowrap">min</span>
              </div>
              <p className="mt-1 text-xs text-accent">
                = {formatMinutesToLabel(timeBetweenVisits)}
              </p>
            </div>
          )}

          <p className="mt-1 text-xs text-content-3">
            Tiempo mínimo que debe transcurrir entre visitas para otorgar puntos.
          </p>
        </div>
      </div>

      <div className="flex items-center justify-between mt-4 p-4 border border-line rounded-lg">
        <div className="flex items-center">
          <span className="text-sm font-medium text-content-2">Permitir acumulación de puntos</span>
          <div className="ml-2 bg-yellow-50 dark:bg-yellow-500/10 px-2 py-1 rounded text-xs font-medium text-yellow-700 dark:text-yellow-300">
            {allowPointsAccumulation ? 'Activo' : 'Inactivo'}
          </div>
        </div>
        <Toggle
          checked={allowPointsAccumulation}
          onChange={() => handleToggleAccumulation()}
          disabled={updating}
          label="Permitir acumulación de puntos"
        />
      </div>
      <p className="mt-2 text-xs text-content-3 pl-4">
        {allowPointsAccumulation
          ? 'Los clientes acumularán puntos en cada visita válida.'
          : 'Los clientes solo podrán canjear los puntos existentes sin acumular nuevos.'}
      </p>
    </div>
  );
};

export default PointsConfigSection;